/**
 * The plan's own three fields — its name, its category and its cover — worded for the top bar.
 *
 * ── The cover is DISPLAY-ONLY as of 15 August 2026 ───────────────────────────────────────────
 * Covers moved to a server-side pool: a plan now carries a `coverId` into that pool and the backend
 * answers with a resolved `coverImageUrl`. This app has no reader for the pool — `/plan-covers` and
 * its picker are a follow-up — so there is nothing to build a grid of, and the panel says so in a
 * sentence instead of drawing a picker over the bundled images, which would let a trainer choose a
 * cover the save cannot express.
 *
 * What still holds is owner rule D3c's first half: every plan has a cover. The trigger draws the
 * server's `coverImageUrl` when there is one and the category's own deterministic bundled pick when
 * there is not, so a plan never shows a blank frame and a category change still changes the picture.
 * The second half — the trainer CHOOSING within a pool — is what is unavailable.
 *
 * So the panel now edits ONE field, the category, and displays the other two.
 *
 * ── No canon, stated ─────────────────────────────────────────────────────────────────────────
 * KANON 11/12 draws a category picker and a cover — in the CREATE modal, which this app already
 * reproduces. Nothing draws either in the builder. So the panel borrows the modal's own chips
 * vocabulary rather than inventing a second one, and the departure is recorded here.
 *
 * **A component below this reads only the view model.** No `t`, no `Intl`, no `draft.category`.
 */

import {
  PLAN_CATEGORY_ORDER,
  TEXT_LIMITS,
  planCategoryKey,
  planDisplayCoverUrl,
  type PlanCategoryKey,
  type PlanDraft,
} from '~/entities/plan-draft';

import { KEY, type Translate } from './builderCopy';

export interface PlanCategoryChipView {
  readonly key: PlanCategoryKey;
  readonly label: string;
  readonly isSelected: boolean;
}

export interface PlanMetaView {
  /**
   * "Typ i okładka programu" — the trigger's accessible name, and it names what the panel OFFERS.
   * It promised the name too while the name was still edited here; the name moved to the `h1` and
   * the label followed it, because a control announcing a field it does not carry sends a screen
   * reader user looking for one.
   */
  readonly triggerLabel: string;
  /** The trapped popup's exit. Base UI asks for one whenever focus is trapped. */
  readonly closeLabel: string;
  /** The hero thumbnail on the trigger, so the control shows what it edits. */
  readonly coverUrl: string;
  readonly title: string;
  /** The plan's name as AUTHORED — what the inline editor in the `h1` opens on. */
  readonly name: string;
  readonly nameLabel: string;
  /** "Naciśnij, aby zmienić nazwę programu" — the `h1` editor's description. */
  readonly renameHint: string;
  /**
   * "Zmień nazwę programu" — the title's CONTEXT-MENU entry.
   *
   * A second string beside `renameHint` rather than a reuse of it: the hint is a description read
   * after a control's name ("Naciśnij, aby…"), and a menu entry is the act itself. The 07.09
   * programator mock draws no rename affordance in the bar, so the menu is the only path and its
   * wording has to stand on its own.
   */
  readonly renameAction: string;
  readonly nameMaxLength: number;
  readonly categoryLabel: string;
  readonly categories: readonly PlanCategoryChipView[];
  /** "Okładka" — never "Grafika", which collides with the "grafik" the assign flow means by schedule. */
  readonly coverLabel: string;
  /**
   * Why there is no cover picker. A STATEMENT, always present — not a refusal attached to a control
   * the trainer can still reach for. See the header.
   */
  readonly coverUnavailable: string;
}

export function toPlanMeta(t: Translate, draft: PlanDraft): PlanMetaView {
  const category = planCategoryKey(draft.category);

  return {
    triggerLabel: t(`${KEY}.meta.trigger`),
    closeLabel: t(`${KEY}.meta.close`),
    // The SERVER's cover first, the bundled category fallback second. `coverImageUrl` is what the
    // backend resolved from the plan's `coverId`, so a cover chosen on the phone shows here; a plan
    // with none still draws its category's deterministic pick rather than a blank frame, which is
    // owner rule D3c's "every plan has a cover" unchanged.
    coverUrl: planDisplayCoverUrl(draft.coverImageUrl, category, draft.name, 'thumb'),
    title: t(`${KEY}.meta.title`),
    name: draft.name,
    nameLabel: t(`${KEY}.meta.nameLabel`),
    renameHint: t(`${KEY}.meta.renameHint`),
    renameAction: t(`${KEY}.meta.renameAction`),
    nameMaxLength: TEXT_LIMITS.planName.max,
    categoryLabel: t(`${KEY}.meta.categoryLabel`),
    categories: PLAN_CATEGORY_ORDER.map((key) => ({
      key,
      // The upstream `plans.category.*`, which the library table and the create modal already read.
      // Three surfaces naming one enum from one key is what stops a re-copy changing two of them.
      label: t(`plans.category.${key}`),
      isSelected: category === key,
    })),
    coverLabel: t(`${KEY}.meta.coverLabel`),
    coverUnavailable: t(`${KEY}.meta.coverUnavailable`),
  };
}
